/**
 * ProjectEditModal — owner-only "Edit project" dialog (T41/T45).
 *
 * Reuses ProjectForm in edit mode and PATCHes through the existing
 * owner-gated endpoint. Invalidating `queryKeys.projects()` covers the
 * detail key too (["projects", id] shares the ["projects"] prefix), which
 * is what keeps the /projects list swatches in sync — the original
 * Cathie-reported bug.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Modal } from "./Modal";
import { ProjectForm } from "./ProjectForm";
import { Button } from "./Button";

export type ProjectEditModalProject = {
  id: string;
  name: string;
  description: string | null;
  kind: "standing" | "initiative";
  status: "active" | "on_hold" | "archived";
  color?: string | null;
  targetDate: string | null;
  tags?: { id: string; name: string; color: string }[];
  healthOverride?: "green" | "amber" | "red" | null;
  healthReason?: string | null;
};

export function ProjectEditModal({
  project,
  open,
  onClose,
}: {
  project: ProjectEditModalProject;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  return (
    <Modal open={open} onClose={onClose} title="Edit project" width={460}>
      <ProjectForm
        onCancel={onClose}
        submitLabel="Save changes"
        initial={{
          name: project.name,
          description: project.description ?? "",
          kind: project.kind,
          status: project.status,
          color: project.color ?? undefined,
          targetDate: project.targetDate ? project.targetDate.slice(0, 10) : "",
          tagNames: (project.tags ?? []).map((t) => t.name),
        }}
        onSubmit={async (v) => {
          await api.patchProject(project.id, {
            name: v.name,
            description: v.description,
            kind: v.kind,
            status: v.status,
            color: v.color,
            targetDate: v.targetDate || null,
            tagNames: v.tagNames,
          });
          await qc.invalidateQueries({ queryKey: queryKeys.projects() });
          // Profile page's project list lives under ["me","projects"] —
          // outside the ["projects"] prefix.
          await qc.invalidateQueries({ queryKey: queryKeys.myProjects() });
          // New tags may have been auto-created server-side.
          await qc.invalidateQueries({ queryKey: queryKeys.tags() });
          onClose();
        }}
      />
      <HealthOverride project={project} />
    </Modal>
  );
}

function HealthOverride({ project }: { project: ProjectEditModalProject }) {
  const qc = useQueryClient();
  const [val, setVal] = useState<"" | "green" | "amber" | "red">(
    project.healthOverride ?? "",
  );
  const [reason, setReason] = useState(project.healthReason ?? "");
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.patchProject(project.id, {
        healthOverride: val || null,
        healthReason: val ? reason.trim() : null,
      }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: queryKeys.projects() });
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div
      style={{
        borderTop: "1px solid var(--border)",
        marginTop: 14,
        paddingTop: 12,
      }}
    >
      <div className="bug-label" style={{ marginBottom: 6 }}>
        Health override
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select
          className="input"
          style={{ width: 120 }}
          value={val}
          onChange={(e) =>
            setVal(e.target.value as "" | "green" | "amber" | "red")
          }
          aria-label="Health override"
        >
          <option value="">Auto</option>
          <option value="green">On track</option>
          <option value="amber">At risk</option>
          <option value="red">Off track</option>
        </select>
        <input
          className="input"
          placeholder="Reason (required to override)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={!val}
          aria-label="Health override reason"
          style={{ flex: 1 }}
        />
        <Button
          variant="secondary"
          onClick={() => save.mutate()}
          disabled={save.isPending || (!!val && !reason.trim())}
        >
          Set
        </Button>
      </div>
      {err && <div className="t-danger t-sm">{err}</div>}
    </div>
  );
}
